import React, { Component } from "react";
import { Configuration, getDataTable } from "../Configuration";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { Modal, Spin } from "antd";
import "antd/dist/antd.css";

const API = Configuration.SYSTEM_SETTINGS.API_URL;

/* Values are concatenated into an EXEC string by getDataTable, and cell content
   is arbitrary user text. Doubling every single quote is what stops one closing
   the literal. Applied to every interpolated value without exception. */
const q = (v) =>
  v === null || v === undefined ? "" : String(v).replace(/'/g, "''");

/* How often the open sheet is checked for outside edits. The probe is one small
   Drive call for modifiedTime, not a read of the contents, so this is cheap —
   and it only runs while somebody actually has the sheet on screen. */
const POLL_MS = 10000;

/* Which sheet is open lives in the URL, not only in component state.

   The sidebar link points at #/frmSheetTracker, the very route the page is
   already on, so clicking it while a sheet was open fired no navigation event
   and nothing reset — the sheet just stayed there. Carrying the open sheet as
   ?tracker=<id> means that click is a real change of location, and the browser
   Back button and a copied link both behave the way people expect. */
function trackerIdFromHash() {
  const h = window.location.hash || "";
  const qm = h.indexOf("?");
  if (qm === -1) return 0;
  const params = h.slice(qm + 1).split("&");
  for (let i = 0; i < params.length; i++) {
    const [k, v] = params[i].split("=");
    if (k === "tracker") return parseInt(v, 10) || 0;
  }
  return 0;
}

function setTrackerHash(id) {
  const base = "#/frmSheetTracker";
  const next = id ? base + "?tracker=" + id : base;
  if (window.location.hash !== next) window.location.hash = next;
}


/* The sheet's own edit URL. rm=minimal drops Google's outer page chrome so the
   grid fills the panel, while keeping the toolbar, filters and dropdowns. The
   tab is selected by gid when one was recorded. */
function sheetEmbedUrl(t) {
  if (!t || !t.SpreadsheetId) return "";
  return "https://docs.google.com/spreadsheets/d/" + t.SpreadsheetId +
         "/edit?rm=minimal&usp=sharing";
}


/* Google's own column labels: A..Z, then AA, AB and so on. */
function colLetter(idx) {
  let s = "", n = idx;
  while (true) {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
    if (n < 0) break;
  }
  return s;
}

const BORDER = "1px solid #d9dde1";
const SS = {
  corner:     { width: 44, background: "#f1f3f4", border: BORDER, padding: 2 },
  colLetter:  { background: "#f1f3f4", border: BORDER, padding: "2px 6px",
                fontSize: 11, fontWeight: 400, color: "#5f6368", textAlign: "center" },
  rowHeadTop: { width: 44, background: "#f1f3f4", border: BORDER, padding: "4px 6px",
                fontSize: 11, fontWeight: 400, color: "#5f6368", textAlign: "center" },
  rowHead:    { width: 44, background: "#f1f3f4", border: BORDER, padding: "4px 6px",
                fontSize: 11, color: "#5f6368", textAlign: "center" },
  headCell:   { background: "#f8f9fa", border: BORDER, padding: "6px 8px",
                fontSize: 12, fontWeight: 600, color: "#202124",
                position: "sticky", top: 0, zIndex: 1, whiteSpace: "nowrap" },
  cell:       { border: BORDER, padding: 0 },
  input:      { width: "100%", border: "none", outline: "none", background: "transparent",
                padding: "5px 8px", fontSize: 13, height: 28 },
};


export default class frmSheetTracker extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loginId: Configuration.USER_ID,
      loading: false,
      busyMsg: "",

      gsa: null,            // saved service account (email only, never the key)
      isAdmin: false,       // comes from SQL, the same test the procs enforce
      trackers: [],

      // add/edit sheet dialog
      sheetModal: false,
      editingId: 0,
      sheetName: "",
      sheetUrl: "",
      tabName: "",
      verifying: false,
      verified: null,

      // service account dialog
      gsaModal: false,
      gsaJson: null,
      gsaFileName: "",

      // open tracker (grid view)
      openTracker: null,
      columns: [],
      rows: [],
      dirty: {},            // rid -> edited record, not yet saved
      lastSync: null,
      /* Typing must never be interrupted, so saving and syncing report through
         this quiet status line instead of the full-screen spinner. The spinner
         is now only used while a sheet is first opening, when there is nothing
         on screen to interrupt. */
      saveState: "idle",    // idle | saving | saved | error
      pendingSaves: 0,
      /* "sheet" embeds Google's own editor, so dropdowns, filters, conditional
         formatting and formulas are the real thing rather than an imitation.
         "table" keeps the database-backed grid for anyone who wants it. */
      viewMode: "sheet",
    };
    this.pollTimer = null;
    this.fileRef = React.createRef();
  }

  componentDidMount() {
    this.loadGsa();
    this.loadTrackers();
    window.addEventListener("hashchange", this.onHashChange);
  }

  componentWillUnmount() {
    this.stopPolling();
    window.removeEventListener("hashchange", this.onHashChange);
    clearTimeout(this._savedTimer);
    clearTimeout(this._pushTimer);
    Object.values(this._saveTimers || {}).forEach(clearTimeout);
  }

  /* The sidebar link drops ?tracker=, which lands here and returns to the
     list. Opening a sheet also passes through here, so there is one path in
     and out rather than two that could disagree. */
  onHashChange = () => {
    const id = trackerIdFromHash();
    const openId = this.state.openTracker ? this.state.openTracker.TrackerID : 0;
    if (id === openId) return;

    if (!id) {
      this.stopPolling();
      this.setState({ openTracker: null, rows: [], columns: [], dirty: {} });
      this.loadTrackers();
      return;
    }
    const t = this.state.trackers.find((x) => x.TrackerID === id);
    if (t) this.openSheet(t, true);
  };

  // ── loads ─────────────────────────────────────────────────────────────────

  loadGsa = async () => {
    const dt = await getDataTable("EXEC dbo.Gsa_Get @loginID = " + this.state.loginId);
    const row = Array.isArray(dt) && dt.length ? dt[0] : null;
    this.setState({ gsa: row, isAdmin: !!(row && row.isAdmin) });
  };

  loadTrackers = async () => {
    this.setState({ loading: true });
    const dt = await getDataTable("EXEC dbo.SheetTracker_List @loginID = " + this.state.loginId);
    const list = Array.isArray(dt) ? dt : [];

    /* The list carries the same fn_SheetAdmin_Is answer the Save/Delete procs
       enforce, so the ACTION column is gated on the very query that draws the
       table rather than on a separate credential lookup that could fail on its
       own and hide the buttons from a real administrator. */
    const next = { trackers: list, loading: false };
    if (list.length) next.isAdmin = !!list[0].isAdmin;
    this.setState(next);

    // Arriving on a link that names a sheet: open it once the list is known.
    const wanted = trackerIdFromHash();
    if (wanted && !this.state.openTracker) {
      const t = list.find((x) => x.TrackerID === wanted);
      if (t) this.openSheet(t, true);
    }
  };

  // ── service account ───────────────────────────────────────────────────────

  onPickGsa = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      let parsed;
      try {
        parsed = JSON.parse(String(ev.target.result || ""));
      } catch (err) {
        Swal.fire("Invalid JSON", "That file is not valid JSON.", "error");
        return;
      }
      // Check it before storing: a downloaded OAuth client is a very easy
      // thing to confuse with a service-account key, and the difference only
      // shows up later as an authentication failure.
      if (parsed.type !== "service_account" || !parsed.client_email) {
        Swal.fire(
          "Not a service account key",
          "This file has no <b>client_email</b> / <b>type: service_account</b>. " +
          "In Google Cloud use <b>Service Accounts → Keys → Add key → JSON</b>, " +
          "not an OAuth client ID.",
          "error"
        );
        return;
      }
      this.setState({ gsaJson: parsed, gsaFileName: f.name });
    };
    reader.readAsText(f);
  };

  saveGsa = async () => {
    const { gsaJson, gsaFileName, loginId } = this.state;
    if (!gsaJson) {
      Swal.fire({ icon: "info", title: "Choose the service account JSON file", timer: 1800, showConfirmButton: false });
      return;
    }
    this.setState({ loading: true });
    const res = await getDataTable(
      "EXEC dbo.Gsa_Save @loginID = " + loginId +
      ", @clientEmail = '" + q(gsaJson.client_email) + "'" +
      ", @projectId = '" + q(gsaJson.project_id || "") + "'" +
      ", @credJson = '" + q(JSON.stringify(gsaJson)) + "'" +
      ", @fileName = '" + q(gsaFileName) + "'"
    );
    this.setState({ loading: false });
    if (res === false) {
      Swal.fire("Not saved", "The service account could not be saved.", "error");
      return;
    }
    this.setState({ gsaModal: false, gsaJson: null, gsaFileName: "" });
    await this.loadGsa();
    Swal.fire({
      icon: "success",
      title: "Service account saved",
      html: "Now share each Google Sheet with:<br/><b>" + gsaJson.client_email + "</b><br/>(Editor access)",
    });
  };

  // ── add / edit a sheet ────────────────────────────────────────────────────

  openAddSheet = () =>
    this.setState({ sheetModal: true, editingId: 0, sheetName: "", sheetUrl: "", tabName: "", verified: null });

  openEditSheet = (t) =>
    this.setState({
      sheetModal: true, editingId: t.TrackerID, sheetName: t.SheetName,
      sheetUrl: t.SheetUrl, tabName: t.TabName || "", verified: null,
    });

  verifySheet = async () => {
    const { sheetUrl, loginId } = this.state;
    if (!sheetUrl.trim()) {
      Swal.fire({ icon: "info", title: "Paste the Google Sheet link first", timer: 1700, showConfirmButton: false });
      return;
    }
    this.setState({ verifying: true, verified: null });
    try {
      const r = await fetch(API + "sheets/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ loginID: loginId, url: sheetUrl.trim() }),
      });
      const d = await r.json();
      this.setState({ verifying: false, verified: d });
      if (!d.ok) Swal.fire("Cannot open that sheet", d.error || "Unknown error", "error");
    } catch (e) {
      this.setState({ verifying: false });
      Swal.fire("Check failed", String(e), "error");
    }
  };

  saveSheet = async () => {
    const { loginId, editingId, sheetName, sheetUrl, tabName, verified } = this.state;
    if (!sheetName.trim()) {
      Swal.fire({ icon: "info", title: "Please enter a sheet name", timer: 1700, showConfirmButton: false });
      return;
    }
    const sid = verified && verified.spreadsheetId
      ? verified.spreadsheetId
      : (sheetUrl.split("/spreadsheets/d/")[1] || "").split(/[/?#]/)[0];

    if (!sid) {
      Swal.fire("Not a Google Sheet link", "Paste the full link from your browser's address bar.", "error");
      return;
    }

    this.setState({ loading: true });
    const res = await getDataTable(
      "EXEC dbo.SheetTracker_Save @loginID = " + loginId +
      ", @trackerID = " + (parseInt(editingId, 10) || 0) +
      ", @sheetName = '" + q(sheetName.trim()) + "'" +
      ", @sheetUrl = '" + q(sheetUrl.trim()) + "'" +
      ", @spreadsheetId = '" + q(sid) + "'" +
      ", @tabName = '" + q(tabName.trim()) + "'"
    );
    this.setState({ loading: false });
    if (res === false) {
      Swal.fire("Not saved", "The sheet could not be saved.", "error");
      return;
    }
    this.setState({ sheetModal: false });
    this.loadTrackers();
  };

  removeSheet = async (t) => {
    const ok = await Swal.fire({
      title: "Remove this sheet?",
      html: "<b>" + t.SheetName + "</b><br/><span style='font-size:12px;color:#888'>" +
            "The Google Sheet itself is not touched — only its link to this app.</span>",
      icon: "warning", showCancelButton: true, confirmButtonText: "Remove", confirmButtonColor: "#d33",
    });
    if (!ok.isConfirmed) return;
    await getDataTable("EXEC dbo.SheetTracker_Delete @loginID = " + this.state.loginId +
                       ", @trackerID = " + t.TrackerID);
    this.loadTrackers();
  };

  // ── open a tracker and keep it in step ────────────────────────────────────

  openSheet = async (t, fromHash) => {
    if (!fromHash) setTrackerHash(t.TrackerID);
    // columns cleared deliberately: the sync below is what establishes them
    this.setState({ openTracker: t, rows: [], columns: [], dirty: {}, loading: true,
                    busyMsg: "Loading " + t.SheetName + "…" });
    await this.runSync(t.TrackerID, true);
    await this.loadRows(t.TrackerID);
    this.setState({ loading: false, busyMsg: "" });
    this.startPolling(t.TrackerID);
  };

  closeSheet = () => {
    // Clearing the hash triggers onHashChange, which does the actual reset —
    // one path back to the list, whether the user used this button, the
    // sidebar, or the browser's Back.
    setTrackerHash(0);
    this.stopPolling();
    this.setState({ openTracker: null, rows: [], columns: [], dirty: {} });
    this.loadTrackers();
  };

  loadRows = async (trackerId) => {
    const dt = await getDataTable(
      "EXEC dbo.SheetRow_List @loginID = " + this.state.loginId +
      ", @trackerID = " + trackerId + ", @includeDeleted = 0"
    );
    const rows = (Array.isArray(dt) ? dt : []).map((r) => {
      let data = {};
      try { data = r.RowData ? JSON.parse(r.RowData) : {}; } catch (e) { data = {}; }
      return { rid: r.Rid, data, version: r.Version, updatedOn: r.UpdatedOn, by: r.UpdatedByName };
    });

    /* Column order: whatever the last sync reported, else the tracker's stored
       header, else every key seen across the rows. The union matters — a row
       that was saved before a column existed simply has no key for it, so
       looking at one row alone would hide the newer column. */
    let cols = this.state.columns || [];
    if (!cols.length) {
      const t = this.state.trackers.find((x) => x.TrackerID === trackerId) || {};
      try { cols = t.Columns ? JSON.parse(t.Columns) : []; } catch (e) { cols = []; }
    }
    const seen = cols.slice();
    rows.forEach((r) => Object.keys(r.data).forEach((k) => {
      if (k && seen.indexOf(k) === -1) seen.push(k);
    }));
    /* Keep any row the user is still editing exactly as they left it. A
       background refresh landing mid-keystroke would otherwise wipe out what
       they were typing. */
    const d = this.state.dirty;
    const merged = rows.map((r) => (d[r.rid] ? { ...r, data: d[r.rid] } : r));
    this.setState({ rows: merged, columns: seen });
  };

  startPolling = (trackerId) => {
    this.stopPolling();
    this.pollTimer = setInterval(async () => {
      // Nothing to reconcile while the real sheet is on screen: those edits go
      // straight to Google and never pass through our copy.
      if (this.state.viewMode !== "table") return;
      // Never poll over unsaved edits: refreshing underneath someone mid-type
      // would throw their work away.
      if (Object.keys(this.state.dirty).length > 0) return;
      try {
        const r = await fetch(API + "sheets/poll/" + trackerId, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ loginID: this.state.loginId }),
        });
        const d = await r.json();
        if (d.ok && d.changed) {
          // No status flicker for changes that arrived from the sheet: the
          // rows simply update underneath, as a collaborator's edit would.
          await this.runSync(trackerId, false);
          await this.loadRows(trackerId);   // external change: re-read is the point
        }
      } catch (e) {
        /* a failed probe is not worth interrupting the user for; the next
           tick will try again */
      }
    }, POLL_MS);
  };

  stopPolling = () => {
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.pollTimer = null;
  };

  runSync = async (trackerId, force) => {
    try {
      const r = await fetch(API + "sheets/sync/" + trackerId, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ loginID: this.state.loginId, force: !!force }),
      });
      const d = await r.json();
      if (!d.ok) {
        Swal.fire("Sync failed", d.error || "Unknown error", "error");
        return false;
      }
      /* Take the header row from the sync we just ran. Reading it from the
         tracker list instead meant a column added to the sheet after the page
         loaded never appeared, because that list was fetched before the sync
         and its stored Columns were one sync behind. */
      const next = { lastSync: new Date().toLocaleTimeString() };
      if (d.columns && d.columns.length) next.columns = d.columns;
      this.setState(next);
      return true;
    } catch (e) {
      Swal.fire("Sync failed", String(e), "error");
      return false;
    }
  };

  // ── editing ───────────────────────────────────────────────────────────────

  editCell = (rid, col, value) => {
    const row = this.state.rows.find((r) => r.rid === rid);
    if (!row) return;
    const current = this.state.dirty[rid] || { ...row.data };
    current[col] = value;
    this.setState({ dirty: { ...this.state.dirty, [rid]: current } });
    this.scheduleSave(rid);
  };

  /* Save shortly after typing stops, per row.

     Saving on every keystroke would be one request per character; saving only
     on blur is what made this feel slow, because nothing was committed until
     the cell was left. A short pause after the last keystroke is the middle
     ground, and moving to another cell flushes it immediately anyway. */
  scheduleSave = (rid) => {
    this._saveTimers = this._saveTimers || {};
    clearTimeout(this._saveTimers[rid]);
    // Short enough to feel immediate, long enough that a word is one request
    // rather than one per letter.
    this._saveTimers[rid] = setTimeout(() => this.flushSave(rid), 250);
  };

  flushSave = (rid) => {
    if (this._saveTimers) clearTimeout(this._saveTimers[rid]);
    if (this.state.dirty[rid]) this.saveRow(rid);
  };

  saveRow = async (rid) => {
    const { loginId, openTracker, dirty } = this.state;
    const row = this.state.rows.find((r) => r.rid === rid);
    const edited = dirty[rid];
    if (!row || !edited) return;

    this.setState({ saveState: "saving", pendingSaves: this.state.pendingSaves + 1 });
    const dt = await getDataTable(
      "EXEC dbo.SheetRow_SaveFromTool @loginID = " + loginId +
      ", @trackerID = " + openTracker.TrackerID +
      ", @rid = '" + q(rid) + "'" +
      ", @rowData = '" + q(JSON.stringify(edited)) + "'" +
      ", @baseVersion = " + row.version
    );
    const res = Array.isArray(dt) && dt.length ? dt[0] : null;

    if (res && res.Status === "CONFLICT") {
      this.setState({ saveState: "error", pendingSaves: Math.max(0, this.state.pendingSaves - 1) });
      let theirs = {};
      try { theirs = res.RowData ? JSON.parse(res.RowData) : {}; } catch (e) {}
      await Swal.fire({
        icon: "warning",
        title: "This row changed while you were editing",
        html:
          "<div style='text-align:left;font-size:13px'>" +
          "<b>" + (res.UpdatedByName || "Someone else") + "</b> saved it first" +
          (res.UpdatedOn ? " (" + String(res.UpdatedOn).substring(0, 19) + ")" : "") + "." +
          "<br/><br/><b>Their version now:</b><br/><span style='font-size:12px'>" +
          Object.keys(theirs).map((k) => k + ": " + theirs[k]).join("<br/>") +
          "</span><br/><br/>Your change was not saved. The row will reload so you " +
          "can re-apply it on top of theirs.</div>",
        confirmButtonText: "Reload the row",
      });
      const d2 = { ...this.state.dirty }; delete d2[rid];
      this.setState({ dirty: d2 });
      await this.loadRows(openTracker.TrackerID);
      return;
    }

    if (res && res.Status === "DELETED") {
      this.setState({ saveState: "error", pendingSaves: Math.max(0, this.state.pendingSaves - 1) });
      Swal.fire("Row was deleted", "Someone removed this row while you were editing it.", "info");
      const d2 = { ...this.state.dirty }; delete d2[rid];
      this.setState({ dirty: d2 });
      await this.loadRows(openTracker.TrackerID);
      return;
    }

    /* Saved. Carry the new version straight into the row.

       This is what stops a burst of typing colliding with itself: each save
       moves the row's version on, so without this the next keystroke would
       still be quoting the old one and the row would appear to conflict with
       the user's own previous save. */
    const newVersion = res && res.Version ? res.Version : row.version + 1;
    const d2 = { ...this.state.dirty }; delete d2[rid];
    this.setState({
      dirty: d2,
      rows: this.state.rows.map((r) =>
        r.rid === rid ? { ...r, data: edited, version: newVersion } : r),
    });

    /* Push to Google on its own, slower beat. The value is already safe in the
       database; batching the sheet write keeps a fast typist from firing a
       Sheets API call per cell. */
    this.schedulePush(openTracker.TrackerID);
  };

  schedulePush = (trackerId) => {
    clearTimeout(this._pushTimer);
    this._pushTimer = setTimeout(() => this.backgroundSync(trackerId, false), 900);
  };

  /* Push to Google and refresh, without blocking anything on screen.
     pendingSaves keeps the status honest when several cells are saved in quick
     succession — "Saved" only appears once the last one has actually landed. */
  backgroundSync = async (trackerId, reload) => {
    // Never let two syncs overlap: a second full sheet read while one is in
    // flight adds latency and can interleave writes.
    if (this._syncing) { this._syncQueued = true; return; }
    this._syncing = true;
    try {
      await this.runSync(trackerId, true);
      /* Deliberately NOT reloading after our own edit. The row on screen is
         already the row we just wrote — re-fetching every row and re-rendering
         the grid after each save is what made fast typing feel heavy. Rows are
         only re-read when polling finds a change that came from the sheet. */
      if (reload) await this.loadRows(trackerId);
      const left = Math.max(0, this.state.pendingSaves - 1);
      this.setState({ pendingSaves: left, saveState: left ? "saving" : "saved" });
      if (!left) {
        clearTimeout(this._savedTimer);
        this._savedTimer = setTimeout(() => {
          if (this.state.pendingSaves === 0) this.setState({ saveState: "idle" });
        }, 2500);
      }
    } catch (e) {
      this.setState({ saveState: "error",
                      pendingSaves: Math.max(0, this.state.pendingSaves - 1) });
    } finally {
      this._syncing = false;
      if (this._syncQueued) {          // edits arrived while this one ran
        this._syncQueued = false;
        this.schedulePush(trackerId);
      }
    }
  };

  addRow = async () => {
    const { loginId, openTracker, columns } = this.state;
    const rid = (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : "r-" + Date.now() + "-" + Math.random().toString(16).slice(2);

    const blank = {};
    columns.forEach((c) => { blank[c] = ""; });

    /* Show the row at once and let the write follow. Waiting for the round
       trip before the row appears is the thing that makes a grid feel unlike a
       spreadsheet, where a new line is simply there. */
    this.setState({
      rows: this.state.rows.concat([{ rid, data: blank, version: 1 }]),
      saveState: "saving",
      pendingSaves: this.state.pendingSaves + 1,
    });

    await getDataTable(
      "EXEC dbo.SheetRow_SaveFromTool @loginID = " + loginId +
      ", @trackerID = " + openTracker.TrackerID +
      ", @rid = '" + q(rid) + "'" +
      ", @rowData = '" + q(JSON.stringify(blank)) + "'" +
      ", @baseVersion = 0"
    );
    this.backgroundSync(openTracker.TrackerID, true);
  };

  deleteRow = async (row) => {
    const { loginId, openTracker } = this.state;
    const ok = await Swal.fire({
      title: "Delete this row?",
      html: "<span style='font-size:12px;color:#888'>It is hidden here and cleared in the " +
            "Google Sheet. Nothing is destroyed — it can be restored.</span>",
      icon: "warning", showCancelButton: true, confirmButtonText: "Delete", confirmButtonColor: "#d33",
    });
    if (!ok.isConfirmed) return;

    this.setState({
      rows: this.state.rows.filter((x) => x.rid !== row.rid),   // gone immediately
      saveState: "saving",
      pendingSaves: this.state.pendingSaves + 1,
    });
    const dt = await getDataTable(
      "EXEC dbo.SheetRow_DeleteFromTool @loginID = " + loginId +
      ", @trackerID = " + openTracker.TrackerID +
      ", @rid = '" + q(row.rid) + "'" +
      ", @baseVersion = " + row.version
    );
    const res = Array.isArray(dt) && dt.length ? dt[0] : null;
    if (res && res.Status === "CONFLICT") {
      this.setState({ saveState: "error",
                      pendingSaves: Math.max(0, this.state.pendingSaves - 1) });
      Swal.fire("Row changed", "Someone edited this row just now — it has been put back so you can check before deleting.", "warning");
      await this.loadRows(openTracker.TrackerID);
      return;
    }
    this.backgroundSync(openTracker.TrackerID, true);
  };

  // ── render ────────────────────────────────────────────────────────────────

  render() {
    const {
      loading, busyMsg, gsa, trackers, openTracker, columns, rows, dirty, lastSync,
      sheetModal, editingId, sheetName, sheetUrl, tabName, verifying, verified,
      gsaModal, gsaFileName,
    } = this.state;

    const hasGsa = !!(gsa && gsa.hasCred);

    return (
      <div className="content-wrapper">
        {/* Only while a sheet is first opening. Once the grid is on screen the
            user is typing in it, and a full-screen spinner there would be the
            very interruption this page is meant to avoid. */}
        {loading && !openTracker && (
          <div className="loader" style={{ display: "block" }}>
            <div className="loader-item"><Spin tip={busyMsg || undefined} /></div>
          </div>
        )}

        <section className="content">
          <div className="box box-success">
            <div className="box-header with-border" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "12px 20px" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: "#aaa", marginBottom: 3 }}>
                  Dashboard &gt; Tools &gt; Sheet Tracker
                  {openTracker ? " > " + openTracker.SheetName : ""}
                </div>
                <h3 className="box-title" style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                  {openTracker ? openTracker.SheetName : "Sheet Tracker"}
                </h3>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4, flexWrap: "wrap" }}>
                {openTracker ? (
                  <>
                    {/* Reads like a document autosave rather than a progress
                        dialog: it states what is happening and never blocks. */}
                    <span style={{
                      fontSize: 11, minWidth: 108, textAlign: "right",
                      color: this.state.saveState === "error" ? "#cf1322" : "#888",
                    }}>
                      {this.state.saveState === "saving" ? "Saving…"
                        : this.state.saveState === "saved" ? "All changes saved"
                        : this.state.saveState === "error" ? "Not saved"
                        : (lastSync ? "Synced " + lastSync : "")}
                    </span>
                    <button className="btn btn-sm btn-default"
                      onClick={() => this.setState({
                        viewMode: this.state.viewMode === "sheet" ? "table" : "sheet" })}>
                      {this.state.viewMode === "sheet" ? "▦ Table view" : "▣ Sheet view"}
                    </button>
                    <a className="btn btn-sm btn-default" href={openTracker.SheetUrl}
                      target="_blank" rel="noopener noreferrer">↗ Open in Google</a>
                    {this.state.viewMode === "table" && (
                      <button className="btn btn-sm btn-default" onClick={this.addRow}>⊕ Add Row</button>
                    )}
                    {this.state.viewMode === "table" && (
                      <button className="btn btn-sm btn-default"
                        onClick={() => {
                          this.setState({ saveState: "saving",
                                          pendingSaves: this.state.pendingSaves + 1 });
                          this.backgroundSync(openTracker.TrackerID, true);
                        }}>⟳ Sync now</button>
                    )}
                    <button className="btn btn-sm btn-default" onClick={this.closeSheet}>← Back</button>
                  </>
                ) : (
                  <>
                    {this.state.isAdmin && (
                      <>
                        <button className="btn btn-sm btn-default" onClick={() => this.setState({ gsaModal: true })}>
                          ⚙ Google Access
                        </button>
                        <button className="btn btn-sm btn-default" onClick={this.openAddSheet} disabled={!hasGsa}>
                          ⊕ Add Sheet
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            <div style={{ background: "#fffbe6", borderBottom: "1px solid #ffe58f", padding: "8px 16px", fontSize: 12, color: "#666" }}>
              {openTracker ? (
                this.state.viewMode === "sheet" ? (
                  <>
                    <b>This is the Google Sheet itself</b> — dropdowns, filters, formatting
                    and formulas all work exactly as they do in your browser, and edits save
                    straight to Google. Use <b>▦ Table view</b> for the simplified grid.
                  </>
                ) : (
                  <>
                    <b>Table view:</b> a simplified grid backed by this application.
                    Change a cell and it saves automatically; changes made in the sheet
                    appear within a few seconds. Use <b>▣ Sheet view</b> for the real sheet.
                  </>
                )
              ) : (
                <>
                  <b>How to use:</b> Use <b>⚙ Google Access</b> once to upload the service
                  account key → share each Google Sheet with that service account email →
                  <b> ⊕ Add Sheet</b> to register it → click a <b>sheet name</b> to open and edit it.
                  Users never need their own access to the sheet.
                </>
              )}
            </div>

            <div className="box-body">
              {/* Google access status */}
              {!openTracker && (
                <div style={{
                  background: hasGsa ? "#f6ffed" : "#fff2f0",
                  border: "1px solid " + (hasGsa ? "#b7eb8f" : "#ffccc7"),
                  borderRadius: 6, padding: "10px 14px", marginBottom: 16, fontSize: 12,
                }}>
                  {hasGsa ? (
                    <>
                      <b style={{ color: "#389e0d" }}>Google access configured.</b>{" "}
                      Share each sheet with{" "}
                      <b style={{ fontFamily: "monospace" }}>{gsa.ClientEmail}</b> (Editor).
                    </>
                  ) : (
                    <>
                      <b style={{ color: "#cf1322" }}>No Google service account saved.</b>{" "}
                      Use <b>⚙ Google Access</b> to upload the JSON key before adding sheets.
                    </>
                  )}
                </div>
              )}

              {/* ── list view ───────────────────────────────────── */}
              {!openTracker && (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th style={{ width: 70 }}>SR NO.</th>
                        <th>SHEET NAME</th>
                        <th style={{ width: 90 }}>ROWS</th>
                        <th style={{ width: 150 }}>LAST SYNC</th>
                        <th style={{ width: 110 }}>STATUS</th>
                        <th style={{ width: 150 }}>ACTION</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trackers.length === 0 && (
                        <tr><td colSpan="6" style={{ textAlign: "center", color: "#999", padding: 22 }}>
                          No sheets registered yet.
                        </td></tr>
                      )}
                      {trackers.map((t, i) => (
                        <tr key={t.TrackerID}>
                          <td>{i + 1}</td>
                          <td>
                            <a onClick={() => this.openSheet(t)} title={t.SheetUrl}
                              style={{ cursor: "pointer", fontWeight: 600, color: "var(--brand-teal, #3D9970)" }}>
                              {t.SheetName}
                            </a>
                          </td>
                          <td>{t.RowCountLive}</td>
                          <td style={{ fontSize: 11, color: "#888" }}>
                            {/* Formatted by SQL: a datetime does not survive the
                                generic query endpoint's JSON intact, which is why
                                this read "never" on a tracker that had synced. */}
                            {t.LastSyncText || "never"}
                          </td>
                          <td>
                            <span style={{
                              fontSize: 11, fontWeight: 700,
                              color: t.SyncStatus === "OK" ? "#389e0d"
                                   : t.SyncStatus === "ERROR" ? "#cf1322" : "#888",
                            }} title={t.SyncMessage || ""}>
                              {t.SyncStatus || "NEVER"}
                            </span>
                          </td>
                          <td>
                            {this.state.isAdmin ? (
                              <>
                                <button className="btn btn-xs btn-default" style={{ marginRight: 4 }}
                                  onClick={() => this.openEditSheet(t)}>Edit</button>
                                <button className="btn btn-xs btn-danger"
                                  onClick={() => this.removeSheet(t)}>Remove</button>
                              </>
                            ) : (
                              <span style={{ fontSize: 11, color: "#bbb" }}>—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ── grid view ───────────────────────────────────── */}
              {openTracker && this.state.viewMode === "sheet" && (
                /* Google's editor itself. Everything the sheet has — dropdowns,
                   filter views, conditional formatting, formulas, merges — is
                   present because this IS the sheet, not a re-implementation of
                   it. Nothing here is synced or version-checked: edits go
                   straight to Google, so there is no second copy to disagree
                   with and no conflict dialog to get in the way. */
                <div style={{ border: "1px solid #d0d7de", borderRadius: 4, overflow: "hidden" }}>
                  <iframe
                    title={openTracker.SheetName}
                    src={sheetEmbedUrl(openTracker)}
                    style={{ width: "100%", height: "calc(100vh - 260px)", minHeight: 420, border: "none", display: "block" }}
                    allow="clipboard-read; clipboard-write"
                  />
                </div>
              )}

              {openTracker && this.state.viewMode === "table" && (

                /* Laid out like a spreadsheet rather than a form: column
                   letters, row numbers, a frozen header and flat gridlines, so
                   it reads the same way the Google Sheet does. Cells commit on
                   Enter or when focus leaves, which is how a spreadsheet
                   behaves — the per-row Save button only appears if something
                   is still unsaved. */
                <div style={{ overflow: "auto", border: "1px solid #d0d7de", borderRadius: 4 }}>
                  {columns.length === 0 ? (
                    <div style={{ textAlign: "center", color: "#999", padding: 24 }}>
                      This sheet has no header row yet. Add one in the Google Sheet,
                      then press <b>⟳ Sync now</b>.
                    </div>
                  ) : (
                    <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
                      <thead>
                        {/* Column letters, as in the sheet itself */}
                        <tr>
                          <th style={SS.corner}></th>
                          {columns.map((c, ci) => (
                            <th key={"L" + c} style={SS.colLetter}>{colLetter(ci)}</th>
                          ))}
                          <th style={SS.colLetter}></th>
                        </tr>
                        <tr>
                          <th style={SS.rowHeadTop}>1</th>
                          {columns.map((c) => <th key={c} style={SS.headCell}>{c}</th>)}
                          <th style={SS.headCell}>ACTION</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length === 0 && (
                          <tr>
                            <td style={SS.rowHead}>2</td>
                            <td colSpan={columns.length + 1}
                                style={{ ...SS.cell, textAlign: "center", color: "#999", padding: 18 }}>
                              No rows yet. Use <b>⊕ Add Row</b>, or type one in the Google Sheet.
                            </td>
                          </tr>
                        )}
                        {rows.map((r, i) => {
                          const edited = dirty[r.rid];
                          const shown = edited || r.data;
                          return (
                            <tr key={r.rid}>
                              <td style={SS.rowHead} title={r.by ? "last edited by " + r.by : ""}>
                                {i + 2}
                              </td>
                              {columns.map((c) => (
                                <td key={c} style={{ ...SS.cell, background: edited ? "#fffbe6" : "#fff" }}>
                                  <input
                                    style={SS.input}
                                    value={shown[c] === undefined ? "" : shown[c]}
                                    onChange={(e) => this.editCell(r.rid, c, e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") { e.target.blur(); } }}
                                    onBlur={() => this.flushSave(r.rid)}
                                  />
                                </td>
                              ))}
                              <td style={{ ...SS.cell, whiteSpace: "nowrap", textAlign: "center" }}>
                                {edited ? (
                                  <>
                                    <button className="btn btn-xs btn-success" style={{ marginRight: 4 }}
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => this.saveRow(r.rid)}>Save</button>
                                    <button className="btn btn-xs btn-default"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => {
                                        const d = { ...dirty }; delete d[r.rid];
                                        this.setState({ dirty: d });
                                      }}>Cancel</button>
                                  </>
                                ) : (
                                  <button className="btn btn-xs btn-danger"
                                    onClick={() => this.deleteRow(r)}>Delete</button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── add / edit sheet ──────────────────────────────────────────── */}
        <Modal
          title={editingId ? "Edit Sheet" : "Add Sheet"}
          visible={sheetModal}
          onCancel={() => this.setState({ sheetModal: false })}
          footer={null}
          width={Math.min(560, window.innerWidth * 0.92)}
          style={{ top: 70 }}
          bodyStyle={{ padding: "16px 20px" }}
          destroyOnClose
        >
          <div className="row">
            <div className="col-xs-12 margintop">
              <label style={{ display: "block", marginBottom: 3 }}>
                Sheet Name <span className="vcode">*</span>
              </label>
              <input className="form-control" value={sheetName} autoFocus
                placeholder="e.g. Daily Sales Tracker"
                onChange={(e) => this.setState({ sheetName: e.target.value })} />
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12 margintop">
              <label style={{ display: "block", marginBottom: 3 }}>
                Google Sheet Link <span className="vcode">*</span>
              </label>
              <div style={{ display: "flex", gap: 6 }}>
                <input className="form-control" style={{ flex: 1 }} value={sheetUrl}
                  placeholder="https://docs.google.com/spreadsheets/d/…"
                  onChange={(e) => this.setState({ sheetUrl: e.target.value, verified: null })} />
                <button className="btn btn-default" style={{ whiteSpace: "nowrap" }}
                  onClick={this.verifySheet} disabled={verifying}>
                  {verifying ? "Checking…" : "Check access"}
                </button>
              </div>
              {verified && verified.ok && (
                <div style={{ fontSize: 11, color: "#389e0d", marginTop: 4 }}>
                  ✓ Opened <b>{verified.title}</b> — tabs: {verified.tabs.join(", ")}
                </div>
              )}
              {verified && !verified.ok && (
                <div style={{ fontSize: 11, color: "#cf1322", marginTop: 4 }}>{verified.error}</div>
              )}
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12 margintop">
              <label style={{ display: "block", marginBottom: 3 }}>Tab name</label>
              <input className="form-control" value={tabName}
                placeholder="leave blank for the first tab"
                onChange={(e) => this.setState({ tabName: e.target.value })} />
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
            <button className="btn btn-default" onClick={() => this.setState({ sheetModal: false })}>Cancel</button>
            <button className="btn btn-success" onClick={this.saveSheet}>
              {editingId ? "Update Sheet" : "Save Sheet"}
            </button>
          </div>
        </Modal>

        {/* ── google access ─────────────────────────────────────────────── */}
        <Modal
          title="Google Access"
          visible={gsaModal}
          onCancel={() => this.setState({ gsaModal: false })}
          footer={null}
          width={Math.min(600, window.innerWidth * 0.92)}
          style={{ top: 70 }}
          bodyStyle={{ padding: "16px 20px" }}
          destroyOnClose
        >
          <div style={{ fontSize: 12, color: "#555", lineHeight: 1.6, marginBottom: 14 }}>
            The app opens your sheets as a <b>service account</b>, so people using this
            application never need their own access to them.
            <ol style={{ paddingLeft: 18, marginTop: 8 }}>
              <li>In Google Cloud, enable the <b>Google Sheets API</b> and the <b>Google Drive API</b>.</li>
              <li>Create a <b>Service Account</b>, then <b>Keys → Add key → JSON</b>.</li>
              <li>Upload that JSON file below.</li>
              <li>Share each Google Sheet with the service account's email, as <b>Editor</b>.</li>
            </ol>
          </div>

          {gsa && gsa.hasCred && (
            <div style={{ background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 6, padding: "8px 12px", fontSize: 12, marginBottom: 12 }}>
              Currently saved: <b style={{ fontFamily: "monospace" }}>{gsa.ClientEmail}</b>
              {gsa.JsonFileName ? <span style={{ color: "#888" }}> ({gsa.JsonFileName})</span> : null}
            </div>
          )}

          <label style={{ display: "block", marginBottom: 3 }}>Service account JSON</label>
          <input type="file" accept=".json,application/json" ref={this.fileRef}
            className="form-control" style={{ paddingTop: 4 }} onChange={this.onPickGsa} />
          {gsaFileName && (
            <div style={{ fontSize: 11, color: "#3D9970", marginTop: 4 }}>Selected: {gsaFileName}</div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
            <button className="btn btn-default" onClick={() => this.setState({ gsaModal: false })}>Cancel</button>
            <button className="btn btn-success" onClick={this.saveGsa}>Save Service Account</button>
          </div>
        </Modal>
      </div>
    );
  }
}
